import {z} from 'zod';
import {organizationIdSchema} from './id';

/**
 * The OAuth client an organization publishes events as, against the GOPACS Flexibility
 * Dispatch VTN. GOPACS issues one per DSO through its own self-service — the credential
 * belongs to the grid operator, not to a stage of this tool, which is why it is stored
 * per organization rather than as one `sst.Secret` for everybody.
 *
 * A client id is an identifier, not a credential: it is handed out beside the secret and
 * is already a plain environment variable for Groupie and Solvie. It lives on the
 * organization row so an administration screen can show which client an organization
 * publishes as without reading a secret store. The secret never appears here — see
 * `services/callie-secret-store.ts`.
 */
export const CALLIE_CLIENT_ID_MAX_LENGTH = 200;

/** Keycloak generates 32 characters; the bound is headroom, not a rule of the protocol. */
export const CALLIE_CLIENT_SECRET_MAX_LENGTH = 500;

export const setCallieCredentialsSchema = z.object({
  clientId: z.string().trim().min(1).max(CALLIE_CLIENT_ID_MAX_LENGTH),
  clientSecret: z.string().trim().min(1).max(CALLIE_CLIENT_SECRET_MAX_LENGTH),
});

export type SetCallieCredentials = z.infer<typeof setCallieCredentialsSchema>;

/**
 * What an administrator may read back, and the whole of it. The secret is not on this
 * shape and cannot be: the API writes it to Secrets Manager and only the publishing
 * sweeps read it again.
 *
 * `clientId` and `updatedAt` are null together — an organization either has a client
 * configured or has not, and a half-set pair is refused by the column CHECK.
 */
export const callieCredentialsSchema = z.object({
  organizationId: organizationIdSchema,
  clientId: z.string().max(CALLIE_CLIENT_ID_MAX_LENGTH).nullable(),
  updatedAt: z.coerce.date().nullable(),
});

export type CallieCredentials = z.infer<typeof callieCredentialsSchema>;

export const isCallieConfigured = (credentials: CallieCredentials): boolean => credentials.clientId !== null;
